import React, { useState } from "react";
import PropTypes from "prop-types";
import { Checkbox } from "@mui/material";
import { CustomButton, CustomDialog, CustomIconButton, icon } from "../../../../../../common/components";
import { translate } from "../../../../../../common/providers";
import { useApi } from "../../../../../../common/hooks";
import { CommitmentDefinitionService } from "../../../../../../api";
import styles from "./CommitmentDefinitionRow.module.css";
import AgentSidePanel from "../../../dsi-form-studio/components/agent-panel/AgentSidePanel";

export default function CommitmentDefinitionRow({ definition, onDelete, onEdit, onRefresh }) {
	const { id, name, themeName, agentId, agentName, isReady } = definition || {};
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [openAgentSidePanel, setOpenAgentSidePanel] = useState(false);

	const { call: updateDefinition } = useApi(CommitmentDefinitionService.updateCommitmentDefinition);
	const { call: getDefinition } = useApi(CommitmentDefinitionService.getCommitmentDefinition);

	const handleOpenDeleteDialog = () => {
		setOpenDeleteDialog((prev) => !prev);
	};

	const handleDelete = () => {
		if (onDelete && typeof onDelete === "function") {
			onDelete(definition);
			setOpenDeleteDialog(false);
		}
	};

	const handleEdit = () => {
		if (onEdit && typeof onEdit === "function") {
			onEdit(definition);
		}
	};

	const handleOpenAgentSelection = () => {
		setOpenAgentSidePanel((prev) => !prev);
	};

	const handleChangeAgent = (newAgent) => {
		getDefinition({ id })
			.then((data) => {
				const payload = {
					name: data.name,
					description: data.description,
					searchStrategy: data.searchStrategy,
					contentExamples: data.contentExamples,
					themeId: data.themeId,
					agentId: newAgent?.id || null,
					isReady: newAgent?.id ? data.isReady : false,
				};
				updateDefinition({ id }, payload)
					.then(() => {
						if (onRefresh && typeof onRefresh === "function") {
							onRefresh();
						}
						setOpenAgentSidePanel(false);
					})
					.catch(console.error);
			})
			.catch(console.error);
	};

	const handleToggleReady = (e) => {
		const newIsReady = e.target.checked;
		if (!agentId && newIsReady) {
			return;
		}
		getDefinition({ id })
			.then((data) => {
				const payload = {
					name: data.name,
					description: data.description,
					searchStrategy: data.searchStrategy,
					contentExamples: data.contentExamples,
					themeId: data.themeId,
					agentId: data.agentId,
					isReady: newIsReady,
				};
				updateDefinition({ id }, payload)
					.then(() => {
						if (onRefresh && typeof onRefresh === "function") {
							onRefresh();
						}
					})
					.catch(console.error);
			})
			.catch(console.error);
	};

	return (
		<div className={styles.row}>
			<div>
				<span>{name}</span>
			</div>
			<div>
				<span>{themeName}</span>
			</div>
			<div>
				<CustomButton variant={agentId ? "outlined" : "contained"} onClick={handleOpenAgentSelection}>
					{agentId && agentName ? agentName : translate("dsi.commitment-definition.select-agent")}
				</CustomButton>
			</div>
			<div className={styles.stateCell}>
				<Checkbox checked={!!isReady} disabled={!agentId} onChange={handleToggleReady} />
				<span>{translate("dsi.commitment-definition.enabled")}</span>
			</div>
			<div className={styles.actions}>
				<CustomIconButton
					icon={icon.faPenToSquare}
					size="sm"
					tooltip={translate("common:btn.edit")}
					variant="outlined"
					onClick={handleEdit}
				/>
				<CustomIconButton
					icon={icon.faTrash}
					size="sm"
					tooltip={translate("common:btn.delete")}
					variant="outlined"
					onClick={handleOpenDeleteDialog}
				/>
			</div>
			<CustomDialog
				iconColor="var(--red-500)"
				iconModel={icon.faExclamationTriangle}
				open={openDeleteDialog}
				subTitle={translate("dsi.commitment-definition.delete-confirmation-text")}
				title={translate("dsi.commitment-definition.delete-confirmation")}
				onClose={handleOpenDeleteDialog}
				onSubmit={handleDelete}
			/>
			<AgentSidePanel
				agent={{ agentId: definition?.agentId, agentTitle: definition?.agentName }}
				open={openAgentSidePanel}
				onClose={handleOpenAgentSelection}
				onSubmit={handleChangeAgent}
			/>
		</div>
	);
}

CommitmentDefinitionRow.propTypes = {
	definition: PropTypes.shape({
		id: PropTypes.number,
		name: PropTypes.string,
		themeName: PropTypes.string,
		agentId: PropTypes.number,
		agentName: PropTypes.string,
		isReady: PropTypes.bool,
	}).isRequired,
	onDelete: PropTypes.func.isRequired,
	onEdit: PropTypes.func.isRequired,
	onRefresh: PropTypes.func.isRequired,
};
